import { SkeletonText } from '@chakra-ui/react'
import { mdiTranslate } from '@mdi/js'
import Icon from '@mdi/react'
import { Link } from '@tanstack/react-router'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'

import FormatQuote from '@/components/assets/FormatQuote'
import GigiIconOutline from '@/components/assets/GigiIconOutline'
import InsertDriveFile from '@/components/assets/InsertDriveFile'
import LocalLibrary from '@/components/assets/LocalLibrary'
import TaskIcon from '@/components/assets/TaskIcon'
import { Box } from '@/components/ui/box'
import { Container } from '@/components/ui/container'
import { Flex } from '@/components/ui/flex'
import { Text } from '@/components/ui/typography'
import { currentEnv } from '@/env/vars'
import useColorModeValue from '@/hooks/useColorModeValue'
import useCustomMediaQuery from '@/hooks/useCustomMediaQuery'
import { wizzardProcesingStrategyAtom } from '@/store/gigiChat/ChatAtom'
import { featuredNewsAtom, isCardVisibleAtom, loadingAtom } from '@/store/news'
import { TProcessingStrategy } from '@/types/gigiChat/ConversationSettings'
import { INewsItem } from '@/types/news'

import NewsCard from './newsUpdates/card/banner'
import DetailsDrawer from './tasksApprovals/detailsDrawer'

const Wizzard = () => {
    const { t } = useTranslation()
    const [, setWizzardProcessingStrategy] = useAtom(wizzardProcesingStrategyAtom)
    const [isCardVisible, setIsCardVisible] = useAtom(isCardVisibleAtom)
    const [isMobile] = useCustomMediaQuery()
    const selectedFeaturedNews: INewsItem = useAtomValue(featuredNewsAtom)
    const loading = useAtomValue(loadingAtom)
    // TO Do: replace hardcoded colors values once we cdetermine how we can pass chakra colors to svg icons component
    const iconsColor = useColorModeValue('var(--chakra-colors-dark-700)', 'var(--chakra-colors-white)')

    const cardStyles = {
        width: isMobile ? '100%' : '276px',
        height: isMobile ? 'auto' : '236px',
        backgroundColor: useColorModeValue('var(--chakra-colors-white)', 'var(--chakra-colors-dark-900)'),
        border: useColorModeValue('1px solid rgb(216, 215, 213)', '1px solid rgba(84, 79, 64, 0.56)'),
        boxShadow: useColorModeValue('0px 0px 8px 1px rgba(84, 79, 64, 0.06)', '0px 0px 8px 1px rgba(84, 79, 64, 0.5)'),
        borderRadius: '12px',
        padding: isMobile ? '16px' : '24px',
    }

    const cardTitle = {
        color: useColorModeValue('var(--chakra-colors-dark-700)', 'var(--chakra-colors-white)'),
        fontSize: '16px',
        fontFamily: 'GSKPrecision-Bold',
        fontWeight: 'bold',
        letterSpacing: '0.25px',
        lineHeight: '24px',
        marginBottom: '8px',
        marginTop: '8px',
    }

    const subtitle = {
        color: useColorModeValue('var(--chakra-colors-dark-700)', 'var(--chakra-colors-white)'),
        fontSize: '14px',
        letterSpacing: '0.25px',
        lineHeight: '20px',
        marginBottom: '16px',
    }

    const content = {
        color: useColorModeValue('rgba(84, 79, 64, 0.7)', 'rgba(255, 255, 255, 0.7)'),
        fontSize: '14px',
        letterSpacing: '0.25px',
        lineHeight: '20px',
        display: isMobile ? 'none' : 'inherit',
    }

    const explanation = {
        borderRadius: '4px',
        width: isMobile ? 'calc(100% - 16px)' : '100%',
        margin: isMobile ? '0 8px' : '0',
    }

    const explanationBox = {
        background: useColorModeValue('var(--chakra-colors-gray-100)', 'var(--chakra-colors-dark-100)'),
        width: isMobile ? '100%' : '856px',
        ml: '10px',
        display: 'flex',
        alignItems: 'center',
        pl: '16px',
        pr: '16px',
        pt: '12px',
        pb: '12px',
        m: '8px',
        borderRadius: '4px',
    }

    const explanationText = {
        color: useColorModeValue('var(--chakra-colors-dark-700)', 'var(--chakra-colors-white)'),
        fontSize: '14px',
        letterSpacing: '0.25px',
        lineHeight: '20px',
    }

    const iconCardTitle = {
        alignItems: 'center',
    }

    const onCardClick = (processingStrategy: TProcessingStrategy) => {
        setWizzardProcessingStrategy(processingStrategy)
        localStorage.removeItem('showWizzard')
    }

    return (
        <>
            <Container
                maxW="100%"
                display="flex"
                flexDirection="column"
                flexGrow="1"
                padding="0"
                backgroundColor={useColorModeValue('transparent', 'black')}
                paddingTop={isMobile ? '24px' : 'inherit'}
                overflow="auto"
            >
                <Flex flexDirection="column" width="100%" justifyContent="center" paddingBottom="48px" flexGrow="1">
                    {currentEnv.features.isEnabled('news') && isCardVisible && (
                        <Flex width="100%" alignItems="center" justifyContent="center">
                            <Box width={isMobile ? '100%' : '856px'} ml={isMobile ? '16px' : '0'} mt="24px">
                                {selectedFeaturedNews?.title ? (
                                    <NewsCard
                                        heading={selectedFeaturedNews.title}
                                        description={selectedFeaturedNews.subtitle}
                                        link="/news"
                                        onClose={() => setIsCardVisible(false)}
                                    />
                                ) : (
                                    loading && <SkeletonText noOfLines={4} />
                                )}
                            </Box>
                        </Flex>
                    )}
                    <Flex width="100%" alignItems="center" justifyContent="center">
                        <Box width={isMobile ? '100%' : '856px'} mb={isMobile ? '8px' : '16px'} ml={isMobile ? '16px' : '0'} mt="24px">
                            <GigiIconOutline />
                        </Box>
                    </Flex>

                    <Flex alignItems="center" justifyContent="center" flexDirection="column" {...explanation} mb="24px">
                        <Box {...explanationBox} mb="8px" minHeight="64px">
                            <Text {...explanationText}>{t('wizzard.explanationText1')}</Text>
                        </Box>
                        <Box {...explanationBox} minHeight="44px">
                            <Text {...explanationText}>{t('wizzard.explanationText2')}</Text>
                        </Box>
                    </Flex>
                    <Box alignSelf="center">
                        <Flex alignItems="center" width="100%" justifyContent="start" flexDirection={isMobile ? 'column' : 'row'}>
                            <Link
                                to="/chat"
                                style={{
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    width: isMobile ? 'calc(100% - 18px)' : '276px',
                                    marginBottom: isMobile ? '8px' : '0',
                                }}
                                onClick={() => onCardClick('QA')}
                            >
                                <Box {...cardStyles}>
                                    {isMobile ? (
                                        <Flex {...iconCardTitle}>
                                            <InsertDriveFile color={iconsColor} style={{ marginRight: '16px', marginBottom: '13px' }} />
                                            <Text {...cardTitle}>{t('wizzard.card1.title')}</Text>
                                        </Flex>
                                    ) : (
                                        <>
                                            <InsertDriveFile color={iconsColor} />
                                            <Text {...cardTitle}>{t('wizzard.card1.title')}</Text>
                                        </>
                                    )}
                                    <Text {...subtitle}>{t('wizzard.card1.subtitle')}</Text>
                                    <Text {...content}>{t('wizzard.card1.description')}</Text>
                                </Box>
                            </Link>
                            <Link
                                to="/chat"
                                style={{
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    width: isMobile ? 'calc(100% - 18px)' : '276px',
                                    marginLeft: isMobile ? '0' : '16px',
                                    marginBottom: isMobile ? '8px' : '0',
                                }}
                                onClick={() => onCardClick('Writing')}
                            >
                                <Box {...cardStyles}>
                                    {isMobile ? (
                                        <Flex {...iconCardTitle}>
                                            <FormatQuote color={iconsColor} style={{ marginRight: '16px', marginBottom: '13px' }} />
                                            <Text {...cardTitle}>{t('wizzard.card2.title')}</Text>
                                        </Flex>
                                    ) : (
                                        <>
                                            <FormatQuote color={iconsColor} />
                                            <Text {...cardTitle}>{t('wizzard.card2.title')}</Text>
                                        </>
                                    )}

                                    <Text {...subtitle}>{t('wizzard.card2.subtitle')}</Text>
                                    <Text {...content}>{t('wizzard.card2.description')}</Text>
                                </Box>
                            </Link>
                            <Link
                                to="/documents"
                                style={{
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    width: isMobile ? 'calc(100% - 18px)' : '276px',
                                    marginLeft: isMobile ? '0' : '16px',
                                    marginBottom: isMobile ? '8px' : '0',
                                }}
                                onClick={() => onCardClick('DocAnalysis')}
                            >
                                <Box {...cardStyles}>
                                    {isMobile ? (
                                        <Flex {...iconCardTitle}>
                                            <LocalLibrary color={iconsColor} style={{ marginRight: '16px', marginBottom: '13px' }} />
                                            <Text {...cardTitle} as="span">
                                                {t('wizzard.card3.title')}
                                            </Text>
                                        </Flex>
                                    ) : (
                                        <>
                                            <LocalLibrary color={iconsColor} />
                                            <Text {...cardTitle}>{t('wizzard.card3.title')}</Text>
                                        </>
                                    )}
                                    <Text {...subtitle}>{t('wizzard.card3.subtitle')}</Text>
                                    <Text {...content}>{t('wizzard.card3.description')}</Text>
                                </Box>
                            </Link>
                        </Flex>
                        <Flex
                            alignItems="center"
                            width="100%"
                            justifyContent="start"
                            flexDirection={isMobile ? 'column' : 'row'}
                            mt={isMobile ? '0' : '16px'}
                        >
                            <Link
                                to="/translate"
                                style={{
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    width: isMobile ? 'calc(100% - 18px)' : '276px',
                                    marginLeft: '0',
                                    marginRight: isMobile ? '0' : '16px',
                                    marginBottom: isMobile ? '8px' : '0',
                                }}
                                onClick={() => onCardClick('Translation')}
                            >
                                <Box {...cardStyles}>
                                    {isMobile ? (
                                        <Flex {...iconCardTitle}>
                                            <Icon
                                                path={mdiTranslate}
                                                size="20px"
                                                color={iconsColor}
                                                style={{ marginRight: '16px', marginBottom: '13px' }}
                                            />
                                            <Text {...cardTitle} as="span">
                                                {t('wizzard.card5.title')}
                                            </Text>
                                        </Flex>
                                    ) : (
                                        <>
                                            <Icon path={mdiTranslate} size="20px" color={iconsColor} />
                                            <Text {...cardTitle}>{t('wizzard.card5.title')}</Text>
                                        </>
                                    )}
                                    <Text {...subtitle}>{t('wizzard.card5.subtitle')}</Text>
                                    <Text {...content}>{t('wizzard.card5.description')}</Text>
                                </Box>
                            </Link>

                            <Link
                                to="/tasks-approvals"
                                style={{
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    width: isMobile ? 'calc(100% - 18px)' : '276px',
                                    marginLeft: '0',
                                    marginRight: isMobile ? '0' : '16px',
                                    marginBottom: isMobile ? '8px' : '0',
                                }}
                            >
                                <Box {...cardStyles}>
                                    {isMobile ? (
                                        <Flex {...iconCardTitle}>
                                            <TaskIcon color={iconsColor} style={{ marginRight: '16px', marginBottom: '13px' }} />
                                            <Text {...cardTitle} as="span">
                                                {t('wizzard.card4.title')}
                                            </Text>
                                        </Flex>
                                    ) : (
                                        <>
                                            <TaskIcon color={iconsColor} />
                                            <Text {...cardTitle}>{t('wizzard.card4.title')}</Text>
                                        </>
                                    )}
                                    <Text {...subtitle}>{t('wizzard.card4.subtitle')}</Text>
                                    <Text {...content}>{t('wizzard.card4.description')}</Text>
                                </Box>
                            </Link>
                        </Flex>
                    </Box>
                </Flex>
            </Container>
            <DetailsDrawer />
        </>
    )
}

export default Wizzard
